import * as React from 'react';
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { parseISO } from 'date-fns';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useGetItemsForAction } from '../../../hooks/use-get-items-for-action';
import { useGetSingleAction } from '../../../hooks/use-get-single-action';
import useTranslation from '../../../components/customHooks/translations';
import { AddActionTextField } from '../../../components/AddActionTextField';
import ImageValidationPanel from '../../../components/logAction/ImageValidationPanel';
import { Button } from '../../../components/Button/Button';
import { LogActionContainer } from '../../../views/LogActionContainer/LogActionContainer';
import { PAGE_PATHS } from '../../../constants/page-paths';
import { getActionFormattedDate } from '../../../utils/get-action-formatted-date';
import { useCreateUserAction } from '../../../hooks/use-create-submitted-action';
import { getLogActionPath } from '../../../constants/log-action-paths';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import theme from '../../../themes';
import { useAvatar } from '../../../hooks/use-get-avatar.hook';

export const EnterDetails = () => {
  const navigate = useNavigate();
  const { actionId } = useParams();
  const translation = useTranslation();
  const { logActionPage, chooseDate } = useTranslation();
  const {
    actionItems,
    loading: itemsLoading,
    hasNoItems,
  } = useGetItemsForAction();
  const { action, actionStyles } = useGetSingleAction();
  const { createUserAction, loading, error: createError } = useCreateUserAction();
  const [validationError, setValidationError] = React.useState(null);

  const { userActionValues, setUserActionValues } = useOutletContext();
  const { avatar, isLoading } = useAvatar();
  const regularTextSx = {
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: 1.5,
    color: '#64748B',
    fontWeight: 400,
  };

  const handleDateChange = (selectedDate) => {
    setUserActionValues({
      ...userActionValues,
      date: getActionFormattedDate(selectedDate),
    });
  };

  const handleItemChange = (event) => {
    const { id, value } = event.target;
    setValidationError(null);

    const updateItemValues = userActionValues?.actionItems.map((item) => {
      if (item.id !== id) return item;
      // Allow decimal input (e.g. 2.5 km) without silently corrupting to 25
      let numberValue = value.replace(/[^\d.]/g, '');
      const parts = numberValue.split('.');
      if (parts.length > 2) {
        numberValue = `${parts[0]}.${parts.slice(1).join('')}`;
      }
      // Cap at a reasonable max to prevent absurd CO2/points
      const MAX_VALUE = 99999;
      if (numberValue !== '' && numberValue !== '.') {
        const parsed = parseFloat(numberValue);
        if (!Number.isNaN(parsed) && parsed > MAX_VALUE) {
          numberValue = String(MAX_VALUE);
        }
      }
      return { id, value: numberValue };
    });

    setUserActionValues({ ...userActionValues, actionItems: updateItemValues });
  };

  const handleImageChange = (image) => {
    setUserActionValues({ ...userActionValues, image });
  };

  const hasRequiredItemValues = () => {
    // Skip validation when action has no items
    if (hasNoItems || actionItems.length === 0) return true;
    return (userActionValues?.actionItems || []).some(
      (item) => String(item?.value ?? '').trim() !== ''
    );
  };

  const onSubmitForm = async (event) => {
    event.preventDefault();
    setValidationError(null);

    if (!hasRequiredItemValues()) {
      setValidationError(
        logActionPage.enterAtLeastOneValue ||
          'Please enter a value for at least one action item.'
      );
      return;
    }

    createUserAction(() => {
      navigate(getLogActionPath('CREATE_ACTION', actionId));
    });
  };

  // On initial render when actionItems are available, update userActionValues
  // to include a blank entry for each actionItem id
  React.useEffect(() => {
    if (actionItems.length > 0 && userActionValues?.actionItems.length === 0) {
      const initialItemValues = actionItems.map((item) => ({
        id: item.id,
        value: '',
      }));

      setUserActionValues((prev) => ({
        ...prev,
        actionItems: initialItemValues,
      }));
    }
  }, [actionItems, userActionValues?.actionItems?.length, setUserActionValues]);

  // On initial render, update userActionValues to display today's date
  React.useLayoutEffect(() => {
    if (!userActionValues?.date) {
      setUserActionValues((prev) => ({
        ...prev,
        date: getActionFormattedDate(),
      }));
    }
  }, [userActionValues?.date, setUserActionValues]);

  return (
    <LogActionContainer
      step={1}
      buttons={
        <>
          <Button
            component={Link}
            to={PAGE_PATHS.LOG_ACTION}
            disabled={loading || itemsLoading}
            variant="outlined"
            sx={{
              border: '1px solid #E2E8F0',
              color: '#0F172A',
              '&:hover': { borderColor: '#0D9488', color: '#0D9488' },
              [theme.breakpoints.down('sm')]: { order: '2' },
            }}
            startIcon={<ArrowBackIosIcon />}
          >
            {translation.back}
          </Button>
          <Button
            disabled={loading || itemsLoading}
            variant="contained"
            form="form-EnterDetails"
            type="submit"
          >
            {translation.next}
          </Button>
        </>
      }
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          alignItems: { xs: 'start', md: 'center' },
          gap: { xs: '1.5rem', md: '2.5rem' },
          '& > figure': {
            flex: '0 0',
            '& > figcaption': {
              backgroundColor: 'rgba(0,0,0,0.5) !important',
              padding: '0.1em 0.35em',
              borderRadius: '0.2em',
            },
          },
        }}
      >
        <Box
          sx={{
            maxWidth: '36rem',
            textAlign: 'left',
          }}
        >
          <Box component="form" id="form-EnterDetails" onSubmit={onSubmitForm}>
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                variant="h1"
                color={actionStyles?.color}
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.4rem' },
                  lineHeight: 1.08,
                  mb: '0.25em',
                }}
              >
                {action?.name}
              </Typography>
            </Box>
            {itemsLoading && (
              <CircularProgress aria-label={logActionPage.loadingActionItems} />
            )}
            {!itemsLoading && !hasNoItems && actionItems.length === 0 ? (
              <Typography variant="p" component="p">
                {logActionPage.noActionItemsAvailable}
              </Typography>
            ) : null}
            {(validationError || createError) && (
              <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                {validationError ||
                  createError?.message ||
                  logActionPage.createActionError ||
                  'Something went wrong while saving your action. Please try again.'}
              </Alert>
            )}
            <Box>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={chooseDate}
                  value={
                    userActionValues?.date
                      ? parseISO(userActionValues.date)
                      : null
                  }
                  onChange={handleDateChange}
                  disableFuture
                  disabled={loading}
                  slotProps={{
                    textField: {
                      disabled: loading,
                      sx: {
                        mt: '0.5rem',
                        '& .MuiInputLabel-root': {
                          ...regularTextSx,
                        },
                      },
                    },
                  }}
                  sx={{
                    mt: '0.5rem',
                    '& .MuiTypography-root': { color: '#0F172A' },
                  }}
                />
              </LocalizationProvider>
              {userActionValues?.actionItems.length > 0 &&
                actionItems?.map((item) => (
                  <AddActionTextField
                    key={item?.id}
                    id={item?.id}
                    label={item?.name}
                    helperText={item.description}
                    value={
                      userActionValues?.actionItems?.find(
                        ({ id }) => id === item?.id
                      )?.value
                    }
                    onChange={handleItemChange}
                    actionStyle={actionStyles}
                    disabled={loading}
                  />
                ))}

              <ImageValidationPanel
                selectedImage={userActionValues?.image}
                setSelectedImage={handleImageChange}
                actionStyle={actionStyles}
                disabled={loading}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ [theme.breakpoints.down('md')]: { margin: '0 auto' } }}>
          {!isLoading && avatar && (
            <Box
              component="img"
              src={avatar.phone || avatar.imageUrl}
              alt=""
              sx={{ width: '100%', maxWidth: { xs: '18rem', md: '26rem' }, height: 'auto' }}
            />
          )}
        </Box>
      </Box>
    </LogActionContainer>
  );
};
